import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// Actions
import { resetCampaignPdfReport } from "../actions/campaign-report/CampaignReportActions";
import {
  resetCityMediasPdfReportsZip,
  resetCityMediasSinglePdfReport,
  resetCityPdfReport,
} from "../actions/campaign-report/CampaignCityReportActions";
import { resetCampaignGraphPdfReport } from "../actions/graph-reports/CampaignGraphReportActions";
import { resetMediaPdfReport } from "../actions/campaign-media/CampaignMediaActions";
import { resetSellerMediaPdfReport } from "../actions/seller/SellerMediaActions";
import { resetOrgMediaPdfReport } from "../actions/org/OrgMediaActions";

// Constants and Utils
import {
  FilenameExtensions,
  ReportConstants,
} from "../constants/GeneralConstants";
import { saveFile } from "../common-utils/file-utils/FileUtils";

// campaign pdf report download effects
export function useCampaignPdfReportDownload(campaignTitle) {
  const dispatch = useDispatch();

  // download pdf report states
  const isCampaignPdfReportDownloaded = useSelector(
    (state) => state.campaignReport.isCampaignPdfReportDownloaded
  );

  const campaignPdfReportData = useSelector(
    (state) => state.campaignReport.campaignPdfReportData
  );
  const { isSentViaEmail, reportData } = campaignPdfReportData || {};

  useEffect(() => {
    if (isCampaignPdfReportDownloaded) {
      // showing toaster of "sentViaEmail" and saving pdf
      if (isSentViaEmail) {
        toast.success("Report has been sent successfully");
      } else {
        const pdfFileName = campaignTitle
          ? `${campaignTitle}${FilenameExtensions.pdf}`
          : ReportConstants.CAMPAIGN_PDF_REPORT;

        // save file
        saveFile(pdfFileName, reportData);
      }

      // Reset data once downloaded
      dispatch(resetCampaignPdfReport());
    }
  }, [isCampaignPdfReportDownloaded]);

  // return
  return null;
}

// city pdf report download effects
export function useCityPdfReportDownload(campaignTitle, cityName) {
  const dispatch = useDispatch();

  // download pdf report states
  const isCityPdfReportDownloaded = useSelector(
    (state) => state.campaignCityReport.isCityPdfReportDownloaded
  );

  const cityPdfReportData = useSelector(
    (state) => state.campaignCityReport.cityPdfReportData
  );
  const { isSentViaEmail, reportData } = cityPdfReportData || {};

  useEffect(() => {
    if (isCityPdfReportDownloaded) {
      // showing toaster of "sentViaEmail" and saving pdf
      if (isSentViaEmail) {
        toast.success("Report has been sent successfully");
      } else {
        const pdfFileName =
          campaignTitle && cityName
            ? `${campaignTitle}_${cityName}${FilenameExtensions.pdf}`
            : ReportConstants.CITY_PDF_REPORT;

        // save file
        saveFile(pdfFileName, reportData);
      }

      // Reset data once downloaded
      dispatch(resetCityPdfReport());
    }
  }, [isCityPdfReportDownloaded]);

  // return
  return null;
}

// city medias single pdf report download effects
export function useCityMediasSinglePdfReportDownload(campaignTitle, cityName) {
  const dispatch = useDispatch();

  // download pdf report states
  const isCityMediasSinglePdfReportDownloaded = useSelector(
    (state) => state.campaignCityReport.isCityMediasSinglePdfReportDownloaded
  );

  const cityMediasSinglePdfReportData = useSelector(
    (state) => state.campaignCityReport.cityMediasSinglePdfReportData
  );
  const { isSentViaEmail, reportData } = cityMediasSinglePdfReportData || {};

  useEffect(() => {
    if (isCityMediasSinglePdfReportDownloaded) {
      // showing toaster of "sentViaEmail" and saving pdf
      if (isSentViaEmail) {
        toast.success("Report has been sent successfully");
      } else {
        const pdfFileName =
          campaignTitle && cityName
            ? `${campaignTitle}_${cityName}_Medias${FilenameExtensions.pdf}`
            : ReportConstants.CITY_MEDIAS_REPORTS;

        // save file
        saveFile(pdfFileName, reportData);
      }

      // Reset data once downloaded
      dispatch(resetCityMediasSinglePdfReport());
    }
  }, [isCityMediasSinglePdfReportDownloaded]);

  // return
  return null;
}

// city medias pdf report zip download effects
export function useCityMediasPdfReportsZipDownload(campaignTitle, cityName) {
  const dispatch = useDispatch();

  // download pdf report states
  const isCityMediasPdfReportsZipDownloaded = useSelector(
    (state) => state.campaignCityReport.isCityMediasPdfReportsZipDownloaded
  );

  const cityMediasPdfReportsZipData = useSelector(
    (state) => state.campaignCityReport.cityMediasPdfReportsZipData
  );
  const { isSentViaEmail, reportData } = cityMediasPdfReportsZipData || {};

  useEffect(() => {
    if (isCityMediasPdfReportsZipDownloaded) {
      // showing toaster of "sentViaEmail" and saving zip
      if (isSentViaEmail) {
        toast.success("Report has been sent successfully");
      } else {
        const pdfFileName =
          campaignTitle && cityName
            ? `${campaignTitle}_${cityName}_Medias${FilenameExtensions.zip}`
            : ReportConstants.CITY_MEDIAS_REPORTS_ZIP;

        // save file
        saveFile(pdfFileName, reportData);
      }

      // Reset data once downloaded
      dispatch(resetCityMediasPdfReportsZip());
    }
  }, [isCityMediasPdfReportsZipDownloaded]);

  // return
  return null;
}

// campaign graph pdf report download effects
export function useCampaignGraphPdfReportDownload(campaignTitle) {
  const dispatch = useDispatch();

  // download pdf report states
  const isCampaignGraphPdfReportDownloaded = useSelector(
    (state) => state.campaignGraphReport.isCampaignGraphPdfReportDownloaded
  );

  const campaignGraphPdfReportData = useSelector(
    (state) => state.campaignGraphReport.campaignGraphPdfReportData
  );
  const { isSentViaEmail, reportData } = campaignGraphPdfReportData || {};

  useEffect(() => {
    if (isCampaignGraphPdfReportDownloaded) {
      // showing toaster of "sentViaEmail" and saving pdf
      if (isSentViaEmail) {
        toast.success("Report has been sent successfully");
      } else {
        const pdfFileName = campaignTitle
          ? `${campaignTitle}_GraphReport${FilenameExtensions.pdf}`
          : ReportConstants.CAMPAIGN_GRAPH_PDF_REPORT;

        // save file
        saveFile(pdfFileName, reportData);
      }

      // Reset data once downloaded
      dispatch(resetCampaignGraphPdfReport());
    }
  }, [isCampaignGraphPdfReportDownloaded]);

  // return
  return null;
}

// campaign media pdf report download effects
export function useCampaignMediaPdfReportDownload(mediaTitle) {
  const dispatch = useDispatch();

  // download pdf report states
  const isCampaignMediaPdfReportDownloaded = useSelector(
    (state) => state.campaignMedia.isCampaignMediaPdfReportDownloaded
  );

  const campaignMediaPdfReportData = useSelector(
    (state) => state.campaignMedia.campaignMediaPdfReportData
  );
  const { isSentViaEmail, reportData } = campaignMediaPdfReportData || {};

  useEffect(() => {
    if (isCampaignMediaPdfReportDownloaded) {
      // showing toaster of "sentViaEmail" and saving pdf
      if (isSentViaEmail) {
        toast.success("Report has been sent successfully");
      } else {
        const pdfFileName = mediaTitle
          ? `${mediaTitle}${FilenameExtensions.pdf}`
          : ReportConstants.MEDIA_PDF_REPORT;

        // save file
        saveFile(pdfFileName, reportData);
      }

      // Reset data once downloaded
      dispatch(resetMediaPdfReport());
    }
  }, [isCampaignMediaPdfReportDownloaded]);

  // return
  return null;
}

// seller media pdf report download effects
export function useSellerMediaPdfReportDownload(mediaTitle) {
  const dispatch = useDispatch();

  // download pdf report states
  const isSellerMediaPdfReportDownloaded = useSelector(
    (state) => state.sellerMedia.isSellerMediaPdfReportDownloaded
  );

  const sellerMediaPdfReportData = useSelector(
    (state) => state.sellerMedia.sellerMediaPdfReportData
  );
  const { isSentViaEmail, reportData } = sellerMediaPdfReportData || {};

  useEffect(() => {
    if (isSellerMediaPdfReportDownloaded) {
      // showing toaster of "sentViaEmail" and saving pdf
      if (isSentViaEmail) {
        toast.success("Report has been sent successfully");
      } else {
        const pdfFileName = mediaTitle
          ? `${mediaTitle}${FilenameExtensions.pdf}`
          : ReportConstants.MEDIA_PDF_REPORT;

        // save file
        saveFile(pdfFileName, reportData);
      }

      // Reset data once downloaded
      dispatch(resetSellerMediaPdfReport());
    }
  }, [isSellerMediaPdfReportDownloaded]);

  // return
  return null;
}

// org media pdf report download effects
export function useOrgMediaPdfReportDownload(mediaTitle) {
  const dispatch = useDispatch();

  // download pdf report states
  const isOrgMediaPdfReportDownloaded = useSelector(
    (state) => state.orgMedia.isOrgMediaPdfReportDownloaded
  );

  const orgMediaPdfReportData = useSelector(
    (state) => state.orgMedia.orgMediaPdfReportData
  );
  const { isSentViaEmail, reportData } = orgMediaPdfReportData || {};

  useEffect(() => {
    if (isOrgMediaPdfReportDownloaded) {
      // showing toaster of "sentViaEmail" and saving pdf
      if (isSentViaEmail) {
        toast.success("Report has been sent successfully");
      } else {
        const pdfFileName = mediaTitle
          ? `${mediaTitle}${FilenameExtensions.pdf}`
          : ReportConstants.MEDIA_PDF_REPORT;

        // save file
        saveFile(pdfFileName, reportData);
      }

      // Reset data once downloaded
      dispatch(resetOrgMediaPdfReport());
    }
  }, [isOrgMediaPdfReportDownloaded]);

  // return
  return null;
}
